import { useCallback, useState } from 'react';

import { useHotRef } from './useHotRef';

export interface Setter<V, Args extends readonly any[] = []> {
  (value: V, ...args: Args): void;
}

export interface UseCascadeStateParams<V = undefined, Args extends readonly any[] = []> {
  initialValue?: V;
  onChange?: (value: V, ...args: Args) => void;
  value?: V;
}

export function useCascadeState<V = undefined, Args extends readonly any[] = []>(
  params: UseCascadeStateParams<V, Args>,
): [V, (value: V, ...args: Args) => void];
export function useCascadeState<V = undefined, Args extends readonly any[] = []>(
  params?: UseCascadeStateParams<V | undefined, Args>,
): [V | undefined, (value: V, ...args: Args) => void];
export function useCascadeState<V = undefined, Args extends readonly any[] = []>(
  params?: UseCascadeStateParams<V | undefined, Args>,
): [V | undefined, (value: V, ...args: Args) => void] {
  const { initialValue, value, onChange } = params ?? {};

  const [uncontrolledValue, setUncontrolledValue] = useState(initialValue);

  const handleChangeRef = useHotRef(((nextValue, ...payload) => {
    setUncontrolledValue(nextValue);
    onChange?.(nextValue, ...payload);
  }) as (value: V, ...args: Args) => void);
  const handleChange = useCallback(
    ((val, ...payload) => handleChangeRef.current(val, ...payload)) as (
      value: V,
      ...args: Args
    ) => void,
    [handleChangeRef],
  );

  const isControlled = value !== undefined;
  return [isControlled ? value : uncontrolledValue, handleChange];
}
